/**
 * TanStack Query client configuration for SvelteKit
 *
 * This module sets up the QueryClient for server state management,
 * caching, and synchronization with proper SSR handling.
 */

import { QueryClient } from '@tanstack/svelte-query';
import { browser } from '$app/environment';

// Create a stable query client instance
// Note: 401 handling is done at the API client level (src/lib/api/client.ts)
function makeQueryClient() {
	return new QueryClient({
		defaultOptions: {
			queries: {
				// With SSR, we usually want to set some default staleTime
				// above 0 to avoid refetching immediately on the client
				staleTime: 60 * 1000, // 1 minute
				// Disable queries during SSR
				enabled: browser,
				// Retry configuration
				retry: (failureCount, error) => {
					// Don't retry on 4xx errors
					if (error && typeof error === 'object' && 'status' in error) {
						const status = (error as any).status;
						if (status >= 400 && status < 500) return false;
					}
					return failureCount < 3;
				},
				retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
			},
			mutations: {
				// Don't retry mutations on 4xx errors (bad request, validation errors)
				// Only retry on network/server errors
				retry: (failureCount, error) => {
					// Don't retry on 4xx errors
					if (error && typeof error === 'object' && 'status' in error) {
						const status = (error as any).status;
						if (status >= 400 && status < 500) return false;
					}
					// Retry once for other errors (network, 5xx)
					return failureCount < 1;
				},
				retryDelay: 2000
			}
		}
	});
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient(): QueryClient {
	if (browser) {
		// Browser: make a new query client if we don't have one yet
		// This is very important so we don't re-make a new client if React
		// suspends during the initial render. This may not be needed if we
		// have a suspense boundary BELOW the creation of the query client
		if (!browserQueryClient) browserQueryClient = makeQueryClient();
		return browserQueryClient;
	} else {
		// Server: always make a new query client
		return makeQueryClient();
	}
}

// Query key factories for consistent caching
export const queryKeys = {
	// Authentication
	auth: {
		user: ['auth', 'user'] as const
	},

	// Facilities
	facilities: {
		all: ['facilities'] as const,
		detail: (id: string) => ['facilities', id] as const,
		status: (id: string) => ['facilities', id, 'status'] as const,
		productionHistory: (id: string) => ['facilities', id, 'production-history'] as const
	},

	// Transactions
	transactions: {
		list: (facilityId: string, filters?: Record<string, any>) =>
			['transactions', facilityId, filters] as const,
		detail: (id: string) => ['transactions', id] as const,
		summary: ['transactions', 'summary'] as const
	},

	// Transaction Statements
	transactionStatements: {
		list: (accountId: string, filters?: Record<string, any>) =>
			['transaction-statements', accountId, filters] as const
	},

	// User accounts
	accounts: {
		mine: ['accounts', 'me'] as const
	},

	// Submissions
	submissions: {
		all: (facilityId?: string) => ['submissions', { facilityId }] as const,
		detail: (id: string) => ['submissions', id] as const,
		pending: ['submissions', 'pending'] as const
	},

	// Dashboard
	dashboard: {
		overview: ['dashboard', 'overview'] as const,
		charts: (period: string) => ['dashboard', 'charts', period] as const
	},

	// Mobile Configuration
	mobileConfig: (hasAuth: boolean) => ['mobile-config', hasAuth] as const,

	// Payment Preferences
	paymentPreference: (facilityId: string) => ['payment-preference', facilityId] as const,

	// Reading Eligibility (readingId optional - used in edit mode to differentiate queries)
	readingEligibility: (facilityId: string, meterId: string, readingId?: string) =>
		['reading-eligibility', facilityId, meterId, readingId] as const,

	// Image Readings (OCR)
	imageReadings: {
		status: (facilityId: string, meterId: string, readingId: string) =>
			['image-readings', facilityId, meterId, readingId] as const
	},

	// User Info
	userInfo: ['user-info'] as const
} as const;
